/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * Actions for Banner component
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.0.0
 */

import {
    IHideBannerPayload,
    IRegistrationClickAlternativeBannerPayload,
    IRegistrationShowAlternativeBannerPayload,
} from '@nexign/scc-ui-kit/lib/components/Banner/types';

import { createAction } from '../app/createAction';
import {
    HIDE_ALTERNATIVE_BANNER,
    HIDE_ALTERNATIVE_BANNER_ERROR,
    HIDE_ALTERNATIVE_BANNER_SUCCESSFUL,
    HIDE_BANNER,
    HIDE_BANNER_ERROR,
    HIDE_BANNER_SUCCESSFUL,
    LOAD_BANNER,
    LOAD_BANNER_FAILED,
    LOAD_BANNER_SUCCESSFUL,
    REGISTRATION_ALTERNATIVE_BANNER_FAILURE,
    REGISTRATION_CLICK_ALTERNATIVE_BANNER,
    REGISTRATION_SHOW_ALTERNATIVE_BANNER,
} from './bannerConstants';
import {
    IHideAlternativeBannerPayload,
    IHideBannerErrorPayload,
    ILoadBannerErrorPayload,
    ILoadBannerSuccessfulPayload,
    IRegistrationAlternativeBannerFailurePayload,
    THideAlternativeBannerAction,
    THideAlternativeBannerErrorAction,
    THideAlternativeBannerSuccessfulAction,
    THideBannerAction,
    THideBannerErrorAction,
    THideBannerSuccessfulAction,
    TLoadBannerAction,
    TLoadBannerErrorAction,
    TLoadBannerSuccessfulAction,
    TRegistrationAlternativeBannerFailure,
    TRegistrationClickAlternativeBannerAction,
    TRegistrationShowAlternativeBannerAction,
} from './bannerTypes';
/*
 * -------------------------------------------
 *           LOAD BANNERS
 * -------------------------------------------
 */

export const loadBanner = (): TLoadBannerAction => createAction(LOAD_BANNER);

export const loadBannerSuccessful = (payload: ILoadBannerSuccessfulPayload): TLoadBannerSuccessfulAction =>
    createAction(LOAD_BANNER_SUCCESSFUL, payload);

export const loadBannerError = (payload: ILoadBannerErrorPayload): TLoadBannerErrorAction =>
    createAction(LOAD_BANNER_FAILED, payload);

/*
 * -------------------------------------------
 *       HIDE BANNER CAMPAIGN
 * -------------------------------------------
 */

export const hideBanner = (payload: IHideBannerPayload): THideBannerAction => createAction(HIDE_BANNER, payload);

export const hideBannerSuccessful = (): THideBannerSuccessfulAction => createAction(HIDE_BANNER_SUCCESSFUL);

export const hideBannerError = (payload: IHideBannerErrorPayload): THideBannerErrorAction =>
    createAction(HIDE_BANNER_ERROR, payload);

/*
 * -------------------------------------------
 *       HIDE ALTERNATIVE BANNER CAMPAIGN
 * -------------------------------------------
 */

export const hideAlternativeBanner = (payload: IHideAlternativeBannerPayload): THideAlternativeBannerAction =>
    createAction(HIDE_ALTERNATIVE_BANNER, payload);

export const hideAlternativeBannerSuccessful = (): THideAlternativeBannerSuccessfulAction =>
    createAction(HIDE_ALTERNATIVE_BANNER_SUCCESSFUL);

export const hideAlternativeBannerError = (payload: IHideBannerErrorPayload): THideAlternativeBannerErrorAction =>
    createAction(HIDE_ALTERNATIVE_BANNER_ERROR, payload);

/*
 * -------------------------------------------
 *       REGISTRATION ALTERNATIVE BANNER
 * -------------------------------------------
 */

export const registrationClickAlternativeBanner = (
    payload: IRegistrationClickAlternativeBannerPayload,
): TRegistrationClickAlternativeBannerAction => createAction(REGISTRATION_CLICK_ALTERNATIVE_BANNER, payload);
export const registrationShowAlternativeBanner = (
    payload: IRegistrationShowAlternativeBannerPayload,
): TRegistrationShowAlternativeBannerAction => createAction(REGISTRATION_SHOW_ALTERNATIVE_BANNER, payload);
export const registrationFailedAlternativeBanner = (
    payload: IRegistrationAlternativeBannerFailurePayload,
): TRegistrationAlternativeBannerFailure => createAction(REGISTRATION_ALTERNATIVE_BANNER_FAILURE, payload);

export type BannerActionsType =
    | THideBannerAction
    | THideBannerErrorAction
    | THideBannerSuccessfulAction
    | TLoadBannerAction
    | TLoadBannerErrorAction
    | TLoadBannerSuccessfulAction
    | THideAlternativeBannerAction
    | THideAlternativeBannerSuccessfulAction
    | THideAlternativeBannerErrorAction
    | TRegistrationClickAlternativeBannerAction
    | TRegistrationShowAlternativeBannerAction
    | TRegistrationAlternativeBannerFailure;
